/**
 * Coordinate System Renderer
 * Zeichnet ein 3-Achsen-Koordinatensystem (X, Y, Z) im isometrischen Raum
 * für ISO.BLOCK, BLOCK.SYSTEM und SYSTEM.TABLE
 */

import { Point } from '@/app/types/cad';

export interface CoordinateSystemOptions {
  /** Länge der Achsenpfeile in Pixeln */
  axisLength?: number;
  /** Dicke der Achsenlinien */
  lineWidth?: number;
  /** Größe der Pfeilspitzen */
  arrowSize?: number;
  /** Farbe der X-Achse (horizontal nach rechts) */
  xColor?: string;
  /** Farbe der Y-Achse (horizontal nach links) */
  yColor?: string;
  /** Farbe der Z-Achse (vertikal nach oben) */
  zColor?: string;
  /** Schriftgröße für Achsenbeschriftungen */
  fontSize?: number;
  /** Zeige Beschriftungen (X, Y, Z) */
  showLabels?: boolean;
  /** Zeige einen kleinen Kreis am Ursprung */
  showOriginDot?: boolean;
}

const DEFAULT_OPTIONS: Required<CoordinateSystemOptions> = {
  axisLength: 100, // Längere Pfeile für bessere Abstände
  lineWidth: 5, // Sehr dicke Linien
  arrowSize: 16, // Große Pfeilspitzen
  xColor: '#ff0000', // Rot für X-Achse (kräftiges Rot)
  yColor: '#00cc00', // Grün für Y-Achse (kräftiges Grün)
  zColor: '#0066ff', // Blau für Z-Achse (kräftiges Blau)
  fontSize: 32, // Große Schrift
  showLabels: true,
  showOriginDot: true,
};

/**
 * Zeichnet das isometrische Koordinatensystem am angegebenen Ursprungspunkt
 * 
 * @param ctx Canvas 2D Context
 * @param origin Ursprungspunkt in Screen-Koordinaten
 * @param options Optionale Darstellungsoptionen
 */
export function drawCoordinateSystem(
  ctx: CanvasRenderingContext2D,
  origin: Point,
  options: CoordinateSystemOptions = {}
): void {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  ctx.save();
  
  // Isometrische Winkel für die drei Achsen
  // X-Achse: 30° nach rechts unten (in isometrischer Projektion: u-Richtung)
  // Y-Achse: 30° nach links unten (in isometrischer Projektion: v-Richtung)
  // Z-Achse: vertikal nach oben
  
  const ISO_ANGLE = Math.PI / 6; // 30 Grad
  
  // X-Achse (u-Richtung): nach rechts unten
  const xEnd: Point = {
    x: origin.x + opts.axisLength * Math.cos(-ISO_ANGLE),
    y: origin.y + opts.axisLength * Math.sin(-ISO_ANGLE),
  };
  
  // Y-Achse (v-Richtung): nach links unten
  const yEnd: Point = {
    x: origin.x + opts.axisLength * Math.cos(-Math.PI + ISO_ANGLE),
    y: origin.y + opts.axisLength * Math.sin(-Math.PI + ISO_ANGLE),
  };
  
  // Z-Achse: vertikal nach oben
  const zEnd: Point = {
    x: origin.x,
    y: origin.y - opts.axisLength,
  };
  
  // X-Achse zeichnen (Rot)
  drawArrow(ctx, origin, xEnd, opts.xColor, opts.lineWidth, opts.arrowSize);
  if (opts.showLabels) {
    drawLabel(ctx, xEnd, 'X', opts.xColor, opts.fontSize);
  }
  
  // Y-Achse zeichnen (Grün)
  drawArrow(ctx, origin, yEnd, opts.yColor, opts.lineWidth, opts.arrowSize);
  if (opts.showLabels) {
    // Y-Label etwas nach unten verschieben für bessere Positionierung
    const yLabelPos: Point = {
      x: yEnd.x,
      y: yEnd.y + 15,  // 15px nach unten
    };
    drawLabelAtPosition(ctx, yLabelPos, 'Y', opts.yColor, opts.fontSize);
  }
  
  // Z-Achse zeichnen (Blau)
  drawArrow(ctx, origin, zEnd, opts.zColor, opts.lineWidth, opts.arrowSize);
  if (opts.showLabels) {
    // Z-Label weiter nach oben und rechts verschieben, damit es nichts verdeckt
    const zLabelPos: Point = {
      x: zEnd.x + 15,  // 15px nach rechts
      y: zEnd.y - 10,  // 10px nach oben
    };
    drawLabelAtPosition(ctx, zLabelPos, 'Z', opts.zColor, opts.fontSize);
  }
  
  // Ursprungspunkt als kleiner Kreis
  if (opts.showOriginDot) {
    ctx.fillStyle = '#333333';
    ctx.beginPath();
    ctx.arc(origin.x, origin.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
}

/**
 * Zeichnet einen Pfeil von start nach end
 */
function drawArrow(
  ctx: CanvasRenderingContext2D,
  start: Point,
  end: Point,
  color: string,
  lineWidth: number,
  arrowSize: number
): void {
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  
  // Hauptlinie
  ctx.beginPath();
  ctx.moveTo(start.x, start.y);
  ctx.lineTo(end.x, end.y);
  ctx.stroke();
  
  // Pfeilspitze
  const angle = Math.atan2(end.y - start.y, end.x - start.x);
  const arrowAngle = Math.PI / 6; // 30 Grad
  
  ctx.beginPath();
  ctx.moveTo(end.x, end.y);
  ctx.lineTo(
    end.x - arrowSize * Math.cos(angle - arrowAngle),
    end.y - arrowSize * Math.sin(angle - arrowAngle)
  );
  ctx.lineTo(
    end.x - arrowSize * Math.cos(angle + arrowAngle),
    end.y - arrowSize * Math.sin(angle + arrowAngle)
  );
  ctx.closePath();
  ctx.fill();
}

/**
 * Zeichnet eine Beschriftung an der angegebenen Position
 */
function drawLabel(
  ctx: CanvasRenderingContext2D,
  position: Point,
  text: string,
  color: string,
  fontSize: number
): void {
  ctx.fillStyle = color;
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  
  // Label mit VIEL größerem Offset vom Pfeilende für bessere Lesbarkeit
  const offset = 24; // Verdoppelt von 12 auf 24
  const angle = Math.atan2(position.y, position.x);
  const labelPos: Point = {
    x: position.x + offset * Math.cos(angle),
    y: position.y + offset * Math.sin(angle),
  };
  
  // Weißer Hintergrund für bessere Lesbarkeit
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 4; // Dickerer Outline
  ctx.strokeText(text, labelPos.x, labelPos.y);
  ctx.fillText(text, labelPos.x, labelPos.y);
}

/**
 * Zeichnet eine Beschriftung an der angegebenen Position
 */
function drawLabelAtPosition(
  ctx: CanvasRenderingContext2D,
  position: Point,
  text: string,
  color: string,
  fontSize: number
): void {
  ctx.fillStyle = color;
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  
  // Weißer Hintergrund für bessere Lesbarkeit
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 4; // Dickerer Outline
  ctx.strokeText(text, position.x, position.y);
  ctx.fillText(text, position.x, position.y);
}

/**
 * Berechnet die optimale Position für das Koordinatensystem
 * basierend auf Canvas-Größe und Viewport
 */
export function getCoordinateSystemPosition(
  canvasWidth: number,
  canvasHeight: number,
  margin: number = 60
): Point {
  // Unten links mit Margin
  return {
    x: margin,
    y: canvasHeight - margin,
  };
}